import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { FiDownload, FiPrinter, FiArrowLeft, FiFileText, FiCalendar, FiUser, FiDollarSign } from 'react-icons/fi';

const PaymentReceipt = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const receiptRef = useRef();
    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    useEffect(() => {
        // Fetch data from the backend using the ID from the URL
        axiosInstance
            .get(`/payments/${id}/receipt`)
            .then((response) => {
                const data = response.data.data;
                console.log('data',data);
                setReceipt(data);
                calculateTotal(data.payment_history);
                setLoading(false);
            })
            .catch((error) => {
                setError(error.response?.data?.message || 'An error occurred while fetching data.');
                setLoading(false);
            });
    }, [id]);

    const calculateTotal = (paymentHistory) => {
        let sum = 0;
        paymentHistory?.forEach((payment) => {
            sum += parseFloat(payment.amount || 0);
        });
        setTotal(sum.toFixed(2));
    };

    const handlePrint = async () => {
        setIsPrinting(true);
        try {
            // Hide action buttons during print
            const actionButtons = document.querySelector('.action-buttons');
            if (actionButtons) actionButtons.style.display = 'none';
            
            await new Promise(resolve => setTimeout(resolve, 100));
            window.print();
            
            // Show action buttons after print
            if (actionButtons) actionButtons.style.display = 'flex';
        } catch (error) {
            console.error('Print error:', error);
        } finally {
            setIsPrinting(false);
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            // Hide action buttons during capture
            const actionButtons = document.querySelector('.action-buttons');
            if (actionButtons) actionButtons.style.display = 'none';
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const canvas = await html2canvas(receiptRef.current, {
                scale: 1.5,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: receiptRef.current.scrollWidth,
                height: receiptRef.current.scrollHeight,
                scrollX: 0,
                scrollY: 0
            });
            
            const imgData = canvas.toDataURL('image/png');
            
            // Use receipt size (80mm width for thermal receipt)
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80, 200] // 80mm width, 200mm initial height
            });
            
            const pdfWidth = 80;
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            
            // Adjust PDF height to fit content
            if (pdfHeight > 200) {
                pdf.internal.pageSize.height = pdfHeight + 10; // Add some margin
            }
            
            pdf.addImage(imgData, 'PNG', 2, 2, pdfWidth - 4, pdfHeight - 4); // 2mm margin on all sides
            
            pdf.save(`payment-receipt-${receipt?.receipt_details?.session_id || id}.pdf`);
            
            // Show action buttons after download
            if (actionButtons) actionButtons.style.display = 'flex';
        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary100 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading receipt...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
            {!error ? (
                <div className="max-w-4xl mx-auto">
                    {/* Action Buttons - Only visible on screen */}
                    <div className="action-buttons flex flex-wrap justify-center gap-2 mb-6 print:hidden">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 font-medium text-sm min-w-0"
                        >
                            <FiArrowLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Back</span>
                        </button>
                        <button
                            onClick={handlePrint}
                            disabled={isPrinting}
                            className="flex items-center gap-1.5 px-3 py-2 bg-primary100 text-white rounded-md hover:bg-primary100/90 transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                        >
                            <FiPrinter className="w-4 h-4" />
                            <span className="hidden sm:inline">{isPrinting ? 'Printing...' : 'Print'}</span>
                        </button>
                        <button
                            onClick={handleDownload}
                            disabled={isDownloading}
                            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                        >
                            <FiDownload className="w-4 h-4" />
                            <span className="hidden sm:inline">{isDownloading ? 'Downloading...' : 'Download PDF'}</span>
                        </button>
                    </div>

                    {/* Receipt Content */}
                    <div ref={receiptRef} className="bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none print:rounded-none" style={{maxWidth: '300px', margin: '0 auto'}}>
                        {/* Header */}
                        <div className="bg-primary100 text-white px-3 py-4 text-center print:bg-gray-800">
                            <div className="flex items-center justify-center mb-2">
                                <FiFileText className="w-5 h-5 mr-2" />
                                <h1 className="text-lg font-bold">{receipt?.system_info?.system_name || 'Payment Receipt'}</h1>
                            </div>
                            <p className="text-green-100 text-xs print:text-gray-300">Payment Receipt</p>
                        </div>

                        {/* Receipt Details */}
                        <div className="p-3">
                            {/* Invoice Information */}
                            <div className="space-y-3 mb-4 text-center">
                                <div className="border-b border-gray-200 pb-2">
                                    <p className="text-xs text-gray-600 font-medium">Invoice #</p>
                                    <p className="text-sm font-semibold text-gray-900">{receipt?.receipt_details?.session_id || '-'}</p>
                                </div>
                                <div className="border-b border-gray-200 pb-2">
                                    <p className="text-xs text-gray-600 font-medium">Patient</p>
                                    <p className="text-sm font-semibold text-gray-900">{receipt?.receipt_details?.patient_name || '-'}</p>
                                </div>
                                <div className="border-b border-gray-200 pb-2">
                                    <p className="text-xs text-gray-600 font-medium">Date</p>
                                    <p className="text-sm font-semibold text-gray-900">{formatDate(receipt?.receipt_details?.created)}</p>
                                </div>
                                <div className="border-b border-gray-200 pb-2">
                                    <p className="text-xs text-gray-600 font-medium">Cashier</p>
                                    <p className="text-sm font-semibold text-gray-900">{receipt?.payment_history?.[0]?.received_by_name || 'System User'}</p>
                                </div>
                            </div>

                            {/* Payment Items */}
                            <div className="mb-4">
                                <div className="text-center mb-3">
                                    <h2 className="text-sm font-bold text-gray-900 border-b border-gray-300 pb-1">PAYMENT DETAILS</h2>
                                </div>
                                
                                <div className="space-y-2">
                                    {receipt?.payment_history?.map((payment, index) => (
                                        <div key={index} className="border-b border-gray-200 pb-2">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <p className="text-xs text-gray-600">{formatDate(payment.created)}</p>
                                                    <p className="text-xs font-medium text-gray-900">{payment.pay_option || '-'}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Total Section */}
                            <div className="border-t-2 border-gray-800 pt-3 mb-3">
                                <div className="flex justify-between items-center">
                                    <div className="text-sm font-bold text-gray-900">TOTAL PAID:</div>
                                    <div className="text-lg font-bold text-gray-900">{formatCurrency(total)}</div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="text-center text-xs text-gray-600 border-t border-gray-200 pt-2">
                                <p>Thank you for your payment!</p>
                                <p className="mt-1">{receipt?.system_info?.system_name || 'Misas App'}</p>
                                <p>Generated: {formatDate(new Date().toISOString())}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Receipt</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary100 text-white rounded-lg hover:bg-primary100/90 transition-colors duration-200 font-medium mx-auto"
                        >
                            <FiArrowLeft className="w-4 h-4" />
                            Go Back
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentReceipt;
